<!-- customer-address-management.component.html -->
<div class="mx-lg p-6">
  <div class="p-6">
    
    <!-- Header -->
    <div class="mb-6">
      <div class="flex justify-between items-center">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">Manage Addresses</h2>
          <p class="text-gray-600 mt-1">Customer: {{ customerName() }}</p>
        </div>
        <button (click)="goBack()" class="button-white">
          ← Back to Customer
        </button>
      </div>
    </div>

    <div class="flex py-4 border-b"></div>

<!-- Existing Addresses Section -->
<div class="mb-6 mt-6">
  <h3 class="text-lg font-semibold text-gray-900 mb-4">Existing Addresses</h3>
  
  @if (existingAddresses().length > 0) {
    <div class="space-y-4">
      @for (address of existingAddresses(); track address.addressId) {
        <div class="border border-gray-300 rounded-lg p-4 bg-white">
          <div class="grid lg:grid-cols-4 md:grid-cols-2 xs:grid-cols-1 gap-4">
            
            <div>
              <label class="text-sm font-medium text-gray-700">Address Line One</label>
              <p class="mt-1 text-gray-900">{{ address.addressLineOne }}</p>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Address Line Two</label>
              <p class="mt-1 text-gray-900">{{ address.addressLineTwo }}</p>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Address Line Three</label>
              <p class="mt-1 text-gray-900">{{ address.addressLineThree || '-' }}</p>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Show As</label>
              <p class="mt-1 text-gray-900">{{ address.show }}</p>
            </div>

            <div class="relative">
              <input type="text" [id]="'existingIdentityRef' + address.addressId" class="textField peer" placeholder=" "
                     maxlength="50"
                     [(ngModel)]="address.identityRef"
                     (ngModelChange)="address.addressId && updateExistingAddressField(address.addressId, 'identityRef', $event)">
              <label class="label" [for]="'existingIdentityRef' + address.addressId">Identity Ref</label>
            </div>

            <div class="relative">
              <input type="text" [id]="'existingContactNo' + address.addressId" class="textField peer" placeholder=" "
                     maxlength="50"
                     [(ngModel)]="address.contactNo"
                     (ngModelChange)="address.addressId && updateExistingAddressField(address.addressId, 'contactNo', $event)">
              <label class="label" [for]="'existingContactNo' + address.addressId">Contact No</label>
            </div>

            <div class="flex flex-col gap-2">
              <div class="flex items-center">
                <input
                  type="checkbox"
                  [id]="'existingUseForInvoice' + address.addressId"
                  [(ngModel)]="address.useForInvoice"
                  (ngModelChange)="address.addressId && updateExistingAddressField(address.addressId, 'useForInvoice', $event)"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                <label 
                  [for]="'existingUseForInvoice' + address.addressId"
                  class="ml-2 text-sm text-gray-700">Available For Invoicing</label>
              </div>
              <div class="flex items-center">
                <input 
                  type="checkbox" 
                  [id]="'existingIsDefaultInvoice' + address.addressId"
                  [(ngModel)]="address.isDefaultInvoice"
                  (ngModelChange)="address.addressId && updateExistingAddressField(address.addressId, 'isDefaultInvoice', $event)"
                  [disabled]="!address.useForInvoice"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50">
                <label 
                  [for]="'existingIsDefaultInvoice' + address.addressId"
                  class="ml-2 text-sm text-gray-700">Invoicing Default</label>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <div class="flex items-center">
                <input 
                  type="checkbox" 
                  [id]="'existingUseForDelivery' + address.addressId"
                  [(ngModel)]="address.useForDelivery"
                  (ngModelChange)="address.addressId && updateExistingAddressField(address.addressId, 'useForDelivery', $event)"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                <label 
                  [for]="'existingUseForDelivery' + address.addressId"
                  class="ml-2 text-sm text-gray-700">Available For Delivery</label>
              </div>
              <div class="flex items-center">
                <input 
                  type="checkbox" 
                  [id]="'existingIsDefaultDelivery' + address.addressId"
                  [(ngModel)]="address.isDefaultDelivery"
                  (ngModelChange)="address.addressId && updateExistingAddressField(address.addressId, 'isDefaultDelivery', $event)"
                  [disabled]="!address.useForDelivery"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50">
                <label 
                  [for]="'existingIsDefaultDelivery' + address.addressId"
                  class="ml-2 text-sm text-gray-700">Delivery Default</label>
              </div>
            </div>
          </div>
          
          <!-- Status Toggle and Update Button -->
          <div class="mt-6 flex items-center justify-between">
            <div class="flex items-center gap-3">
              @if (!address.status) {
                <p class="text-xs text-red-500">
                  Address is inactive. Toggle to enable.
                </p>
              }
              <span class="text-sm font-medium text-gray-700">
                {{ address.status ? 'Active' : 'Inactive' }}
              </span>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  [checked]="address.status"
                  (change)="toggleAddressStatus(address)"
                  class="sr-only peer">
                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>

            <button 
              (click)="updateExistingAddress(address)"
              class="button-blue">
              Update Address
            </button>
          </div>
        </div>
      }
    </div>
  } @else {
    <p class="text-gray-500 text-center py-4">No existing addresses found.</p>
  }
</div>

    <div class="flex py-4 border-b"></div>

    <!-- New Addresses Section -->
    <div class="mb-6 mt-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold text-gray-900">Add New Addresses</h3>
        <button (click)="addNewAddress()" type="button" class="button-blue">
          Add Address
        </button>
      </div>

      @if (newAddresses().length > 0) {
        <div class="space-y-4">
          @for (address of newAddresses(); track $index) {
            <div class="border border-gray-200 rounded-lg p-4 bg-blue-50">
              <div class="flex justify-between items-center mb-3">
                <h4 class="font-medium text-gray-700">New Address {{ $index + 1 }}</h4>
                <button (click)="removeNewAddress($index)" type="button" 
                        class="text-red-600 hover:text-red-800 text-sm font-medium">
                  Remove
                </button>
              </div>
              
              <div class="grid lg:grid-cols-4 md:grid-cols-2 xs:grid-cols-1 gap-4">
                
                <!-- Address Line One -->
                <div class="relative">
                  <input type="text" [id]="'addressLineOne' + $index" class="textField peer" placeholder=" "
                         [(ngModel)]="address.addressLineOne"
                         (ngModelChange)="updateNewAddressField($index, 'addressLineOne', $event)"
                         required>
                  <label class="label" [for]="'addressLineOne' + $index">
                    Address Line One <span class="text-red-700">*</span>
                  </label>
                </div>

                <!-- Address Line Two -->
                <div class="relative">
                  <input type="text" [id]="'addressLineTwo' + $index" class="textField peer" placeholder=" "
                         [(ngModel)]="address.addressLineTwo"
                         (ngModelChange)="updateNewAddressField($index, 'addressLineTwo', $event)"
                         required>
                  <label class="label" [for]="'addressLineTwo' + $index">
                    Address Line Two <span class="text-red-700">*</span>
                  </label>
                </div>

                <!-- Address Line Three -->
                <div class="relative">
                  <input type="text" [id]="'addressLineThree' + $index" class="textField peer" placeholder=" "
                         [(ngModel)]="address.addressLineThree"
                         (ngModelChange)="updateNewAddressField($index, 'addressLineThree', $event)">
                  <label class="label" [for]="'addressLineThree' + $index">Address Line Three</label>
                </div>

                <!-- Country -->
                <app-search-country 
                        [disabled]=false
                        [resetTrigger]="resetSearchTrigger()"
                        [countryInput]="address.country"
                        (selectCountry)="onNewAddressCountrySelected($index, $event)">
                </app-search-country>

                <!-- Show As -->
                <div class="relative">
                  <input type="text" [id]="'show' + $index" class="textField peer" placeholder=" "
                         [(ngModel)]="address.show"
                         (ngModelChange)="updateNewAddressField($index, 'show', $event)"
                         required>
                  <label class="label" [for]="'show' + $index">Show As <span class="text-red-700">*</span></label>
                </div>

                <!-- Identity Ref -->
                <div class="relative">
                  <input type="text" [id]="'identityRef' + $index" class="textField peer" placeholder=" "
                         maxlength="50"
                         [(ngModel)]="address.identityRef"
                         (ngModelChange)="updateNewAddressField($index, 'identityRef', $event)">
                  <label class="label" [for]="'identityRef' + $index">Identity Ref</label>
                </div>

                <!-- Contact No -->
                <div class="relative">
                  <input type="text" [id]="'contactNo' + $index" class="textField peer" placeholder=" "
                         maxlength="50"
                         [(ngModel)]="address.contactNo"
                         (ngModelChange)="updateNewAddressField($index, 'contactNo', $event)">
                  <label class="label" [for]="'contactNo' + $index">Contact No</label>
                </div>

                <!-- Checkboxes -->
                <div class="flex flex-col gap-2">
                  <div class="flex items-center">
                    <input type="checkbox" [id]="'useForInvoice' + $index"
                           [(ngModel)]="address.useForInvoice"
                           (ngModelChange)="updateNewAddressField($index, 'useForInvoice', $event)"
                           class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                    <label [for]="'useForInvoice' + $index" 
                           class="ml-2 text-sm font-medium text-gray-900">Available For Invoicing</label>
                  </div>
                  <div class="flex items-center">
                    <input type="checkbox" [id]="'isDefaultInvoice' + $index"
                           [(ngModel)]="address.isDefaultInvoice"
                           (ngModelChange)="updateNewAddressField($index, 'isDefaultInvoice', $event)"
                           class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                    <label [for]="'isDefaultInvoice' + $index" 
                           class="ml-2 text-sm font-medium text-gray-900">Invoicing Make Default</label>
                  </div>
                </div>

                <div class="flex flex-col gap-2">
                  <div class="flex items-center">
                    <input type="checkbox" [id]="'useForDelivery' + $index"
                           [(ngModel)]="address.useForDelivery"
                           (ngModelChange)="updateNewAddressField($index, 'useForDelivery', $event)"
                           class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                    <label [for]="'useForDelivery' + $index" 
                           class="ml-2 text-sm font-medium text-gray-900">Available For Delivery</label>
                  </div>

                  <div class="flex items-center">
                    <input type="checkbox" [id]="'isDefaultDelivery' + $index"
                           [(ngModel)]="address.isDefaultDelivery"
                           (ngModelChange)="onNewDeliveryDefaultChange($index, $event)"
                           [disabled]="!address.useForDelivery"
                           class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50">
                    <label [for]="'isDefaultDelivery' + $index" 
                           class="ml-2 text-sm font-medium text-gray-900">Delivery Make Default</label>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      } @else {
        <p class="text-gray-500 text-center py-4">No new addresses added. Click "Add Address" to create one.</p>
      }
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end space-x-3 mt-6">
      <button (click)="goBack()" class="button-white">
        Cancel
      </button>
      <button
        (click)="saveNewAddresses()"
        [disabled]="isValidNewAddresses()"
        class="button-blue">
        Save New Addresses
      </button>
    </div>
  </div>
</div>